import { params, executeSearchUrl, displayResults } from '../search-main';

const filterType = 'searched_location';
const filterDefaultText = 'Location';
let eventsApplied = false;

declare var gBrandedSearch: any;

/**
 * Display locations filter
 */
export function displaySearchedLocationFilter() {
    const filterContainer = document.querySelector<HTMLElement>(`#filter-${filterType}-container`);
    const filterButton = document.querySelector<HTMLButtonElement>(`#filter-button-${filterType}`);
    const filterButtonText = document.querySelector<HTMLElement>(`#filter-button-text-${filterType}`);
    const filterList = document.querySelector<HTMLUListElement>('#filter-searched-location-list');
    const clearButton = document.querySelector<HTMLButtonElement>(`#clear-filters-${filterType}`);
    const applyButton = document.querySelector<HTMLButtonElement>(`#apply-filters-${filterType}`);
    const brandedLocations = getBrandedLocations();
    const locationNames: string[] = Array.isArray(params.LocationName) ? params.LocationName as string[] : [];
    const searchedLocations = getUniqueLocations(locationNames);
    const hasRemovableLocations = searchedLocations.some(location => !isBrandedLocation(location, brandedLocations));

    if (!filterContainer || !filterButton || !filterButtonText || !filterList) {
        return;
    }

    if (!eventsApplied) {
        clearButton?.addEventListener('click', clearLocationsClicked);
        applyButton?.addEventListener('click', applyLocationsClicked);
        eventsApplied = true;
    }

    if (searchedLocations.length === 0) {
        filterList.innerHTML = '';
        filterContainer.classList.add('hidden');
        clearButton?.classList.add('hidden');
        setFilterButtonState(filterButton, filterButtonText, [] as string[]);
        return;
    }

    buildLocationList(filterList, searchedLocations, brandedLocations);
    filterContainer.classList.remove('hidden');
    clearButton?.classList.toggle('hidden', !hasRemovableLocations);
    setFilterButtonState(filterButton, filterButtonText, searchedLocations);
}

function buildLocationList(filterList: HTMLUListElement, searchedLocations: string[], brandedLocations: string[]) {
    const fragment = document.createDocumentFragment();

    filterList.innerHTML = '';
    searchedLocations.forEach((location, index) => {
        const listItem = document.createElement('li');
        const input = document.createElement('input');
        const label = document.createElement('label');
        const inputId = `${filterType}-${index}`;
        const isFixedLocation = isBrandedLocation(location, brandedLocations);

        listItem.className = 'flex items-center';
        input.type = 'checkbox';
        input.id = inputId;
        input.name = filterType;
        input.value = location;
        input.checked = true;
        input.disabled = isFixedLocation;

        label.setAttribute('for', inputId);
        label.className = isFixedLocation ? 'cursor-not-allowed text-gray-dark' : 'cursor-pointer';
        label.textContent = location;

        listItem.appendChild(input);
        listItem.appendChild(label);
        fragment.appendChild(listItem);
    });

    filterList.appendChild(fragment);
}

function applyLocationsClicked() {
    const brandedLocations = getBrandedLocations();
    const checkedLocations: string[] = Array.from(document.querySelectorAll<HTMLInputElement>(`[name="${filterType}"]`))
        .filter(input => !input.disabled && input.checked)
        .map(input => input.value);
    const selectedLocations = getUniqueLocations(brandedLocations.concat(checkedLocations));

    params.LocationName = selectedLocations;
    if (selectedLocations.length === 0) {
        params.Radius = undefined;
    }

    params.Page = '1';
    displayResults(executeSearchUrl, false, true);
}

function clearLocationsClicked() {
    params.LocationName = getBrandedLocations();
    if (params.LocationName.length === 0) {
        params.Radius = undefined;
    }
    params.Page = '1';
    displayResults(executeSearchUrl, false, true);
}

function setFilterButtonState(filterButton: HTMLButtonElement, filterButtonText: HTMLElement, searchedLocations: string[]) {
    if (searchedLocations.length === 0) {
        filterButtonText.textContent = filterDefaultText;
        filterButton.classList.remove('button-search-filter-applied');
        filterButton.classList.add('button-search-filter');
        filterButton.querySelectorAll<HTMLElement>('svg').forEach(icon => {
            icon.classList.remove('fill-white');
            icon.classList.add('fill-gray-darkest');
        });
        return;
    }

    filterButtonText.textContent = searchedLocations[0];
    if (searchedLocations.length > 1) {
        filterButtonText.textContent += ` +${searchedLocations.length - 1}`;
    }

    filterButton.classList.add('button-search-filter-applied');
    filterButton.classList.remove('button-search-filter');
    filterButton.querySelectorAll<HTMLElement>('svg').forEach(icon => {
        icon.classList.add('fill-white');
        icon.classList.remove('fill-gray-darkest');
    });
}

function normalizeLocation(location?: string | null) {
    return location?.trim().toLowerCase() ?? '';
}

function getUniqueLocations(locations: string[]): string[] {
    const seenLocations = new Set<string>();
    const uniqueLocations: string[] = [];

    locations.forEach(location => {
        const trimmedLocation = location.trim();
        if (trimmedLocation.length === 0) {
            return;
        }

        const normalizedLocation = normalizeLocation(trimmedLocation);
        if (seenLocations.has(normalizedLocation)) {
            return;
        }

        seenLocations.add(normalizedLocation);
        uniqueLocations.push(trimmedLocation);
    });

    return uniqueLocations;
}

function getBrandedLocations(): string[] {
    const brandedLocations: string[] = Array.isArray(gBrandedSearch?.LocationName) ? gBrandedSearch.LocationName as string[] : [];
    return getUniqueLocations(brandedLocations.filter((location): location is string => typeof location === 'string'));
}

function isBrandedLocation(location: string, brandedLocations: string[]) {
    const normalizedLocation = normalizeLocation(location);
    return brandedLocations.some(brandedLocation => normalizeLocation(brandedLocation) === normalizedLocation);
}
